import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import {
  BUGCROWD_INTEGRATION_PATH_NAME,
  CROWDSTRIKE_INTEGRATION_PATH_NAME,
  HACKERONE_INTEGRATION_PATH_NAME,
  INTEGRATIONS_PATH_NAME,
  INTEGRATION_TYPES,
  JENKINS_INTEGRATION_PATH_NAME,
  SIEM_INTEGRATION_PATH_NAME,
  TORQ_INTEGRATION_PATH_NAME,
  XPANSE_INTEGRATION_PATH_NAME,
  UPWIND_INTEGRATION_PATH_NAME,
  WIZ_INTEGRATION_PATH_NAME,
  PREVENTION_INTEGRATION_PATH_NAME,
  CLOUD_INTEGRATION_PATH_NAME,
} from './integrations.constants';
import { INTEGRATION_FLOWS } from './integration-flows';
import { hasTicketingProviderSettingsKeys } from './provider-settings.utils';
import {
  WizApplicationMappingsSchema,
  WizSelectableObjectsQuerySchema,
  WizSelectableObjectsResponseSchema,
} from './wiz/wiz-application-mapping.schema';
import {
  WizMappingSuggestionsQuerySchema,
  WizMappingSuggestionsResponseSchema,
} from './wiz/wiz-mapping-suggestion.schema';
import { HackeroneReportStateSchema } from './hackerone/hackerone-report.schema';
import {
  XpanseConnectBodySchema,
  XpanseConnectionStatusResponseSchema,
  XpanseUpdateSettingsBodySchema,
} from './xpanse/xpanse.schema';
import {
  CrowdStrikeConnectBodySchema,
  CrowdStrikeConnectionStatusResponseSchema,
} from './crowdstrike/crowdstrike.schema';
import {
  SiemConnectBodySchema,
  SiemConnectionStatusResponseSchema,
  SiemProviderParamSchema,
} from './siem/siem.schema';
import {
  TorqConnectBodySchema,
  TorqConnectionStatusResponseSchema,
} from './torq/torq.schema';
import {
  CloudConnectBodySchema,
  CloudConnectionStatusResponseSchema,
  CloudProviderParamSchema,
  CloudUpdateFlowsBodySchema,
  CloudUpdateInventorySettingsBodySchema,
  CreatePreventionTargetMappingBodySchema,
  DiscoverPreventionWafTargetsQuerySchema,
  DiscoverPreventionWafTargetsResponseSchema,
  ListPreventionTargetMappingsQuerySchema,
  PreventionActivateBodySchema,
  PreventionApplicationIdParamSchema,
  PreventionConnectBodySchema,
  PreventionConnectionDtoSchema,
  PreventionConnectionIdParamSchema,
  PreventionConnectionStatusResponseSchema,
  PreventionControlDtoSchema,
  PreventionControlIdParamSchema,
  PreventionControlTestBodySchema,
  PreventionControlTestResultSchema,
  PreventionControlTestTemplateSchema,
  PreventionListConnectionsQuerySchema,
  PreventionListQuerySchema,
  PreventionProviderParamSchema,
  PreventionRejectBodySchema,
  PreventionTargetMappingDtoSchema,
  PreventionTargetMappingIdParamSchema,
  PreventionWafSuggestionsResponseSchema,
  UpdatePreventionConnectionBodySchema,
} from './prevention/prevention.schema';
import {
  UpwindConnectBodySchema,
  UpwindConnectionStatusResponseSchema,
  UpwindMappingsResponseSchema,
} from './upwind/upwind.schema';
import {
  UpwindSelectableResourcesQuerySchema,
  UpwindSelectableResourcesResponseSchema,
} from './upwind/upwind-application-mapping.schema';
import { BugcrowdSubmissionStateSchema } from './bugcrowd/bugcrowd-submission.schema';
import {
  BugBountyConnectResponseSchema,
  BugcrowdScanTriggerResponseSchema,
} from './bug-bounty/responses';
import { BugBountyImportFailedRowsResponseSchema } from './signal-import/signal-import-failed-rows.schema';
import {
  CreateIntegrationConfigurationProfileBodySchema,
  IntegrationConfigurationProfileDtoSchema,
  IntegrationConfigurationProfileIdParamSchema,
  IntegrationConfigurationProfileListResponseSchema,
  IntegrationConfigurationProfileSampleIssueResponseSchema,
  UpdateIntegrationConfigurationProfileBodySchema,
} from './integration-configuration-profile.schema';
import { TicketingProviderSettingsSchema } from './ticketing-provider-settings.schema';
import type { TicketingProviderSettings } from './ticketing-provider-settings.schema';

const TicketingProviderSchema = z.enum([
  INTEGRATION_TYPES.LINEAR,
  INTEGRATION_TYPES.JIRA,
  INTEGRATION_TYPES.SERVICENOW,
  INTEGRATION_TYPES.GITHUB_ISSUES,
]);

export const MessagingProviderSchema = z.enum([
  INTEGRATION_TYPES.SLACK,
  INTEGRATION_TYPES.MICROSOFT_TEAMS,
  INTEGRATION_TYPES.PAGERDUTY,
]);

export type MessagingProvider = z.infer<typeof MessagingProviderSchema>;

const IntegrationOAuthProviderSchema = z.union([
  TicketingProviderSchema,
  MessagingProviderSchema,
]);

const TicketingProviderParamSchema = z.object({
  provider: TicketingProviderSchema,
});

const ProviderConfigurationProfileParamsSchema =
  TicketingProviderParamSchema.extend(
    IntegrationConfigurationProfileIdParamSchema.shape,
  );

const IntegrationOAuthProviderParamSchema = z.object({
  provider: IntegrationOAuthProviderSchema,
});

const MessagingProviderParamSchema = z.object({
  provider: MessagingProviderSchema,
});

const ApplicationMessagingChannelParamSchema = z.object({
  provider: MessagingProviderSchema,
  applicationId: z.string(),
});

const SearchEngineProviderSchema = z.enum([INTEGRATION_TYPES.GLEAN]);

const SearchEngineProviderParamSchema = z.object({
  provider: SearchEngineProviderSchema,
});

const SearchEngineConnectBodySchema = z.object({
  instance: z.string().min(1),
  apiToken: z.string().min(1),
});

const WizConnectBodySchema = z.object({
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  apiUrl: z.url(),
  authUrl: z.url().optional(),
});

const WizRuleHealthSchema = z.object({
  id: z.string().nullable(),
  status: z.enum(['ACTIVE', 'MISSING']),
});

const WizConnectionStatusResponseSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.WIZ),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  providerUserId: z.string().nullable(),
  apiUrl: z.string().nullable(),
  authUrl: z.string().nullable(),
  rules: z.object({
    issues: WizRuleHealthSchema,
    detections: WizRuleHealthSchema,
    threats: WizRuleHealthSchema,
  }),
});

const WizMappingsResponseSchema = z.object({
  mappings: WizApplicationMappingsSchema,
});

const IntegrationConnectionNameSchema = z.string().trim().min(1).max(100);

const HackeroneConnectBodySchema = z.object({
  apiUsername: z.string().min(1),
  apiToken: z.string().min(1),
  programHandle: z.string().min(1),
  webhookSecret: z.string().min(1),
  reportStates: z.array(HackeroneReportStateSchema).optional(),
  name: IntegrationConnectionNameSchema.optional(),
});

const HackeroneConnectionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  programHandle: z.string().nullable(),
  providerUserId: z.string().nullable(),
  webhookUrl: z.string().nullable(),
});

const HackeroneConnectionStatusResponseSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.HACKERONE),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  providerUserId: z.string().nullable(),
  programHandle: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  connections: z.array(HackeroneConnectionSchema),
});

const BugcrowdConnectBodySchema = z.object({
  apiUsername: z.string().min(1),
  apiToken: z.string().min(1),
  submissionStates: z.array(BugcrowdSubmissionStateSchema).optional(),
  name: IntegrationConnectionNameSchema.optional(),
});

const BugcrowdConnectionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  providerUserId: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
});

const BugBountyRenameConnectionBodySchema = z.object({
  connectionId: z.string().min(1),
  name: IntegrationConnectionNameSchema,
});

const BugcrowdConnectionStatusResponseSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.BUGCROWD),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  providerUserId: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  connections: z.array(BugcrowdConnectionSchema),
});

const BugBountyScanBodySchema = z.object({
  connectionId: z.string().min(1).optional(),
});

const BugBountyConnectionIdBodySchema = z.object({
  connectionId: z.string().min(1),
});

const JenkinsConnectResponseSchema = z.object({
  success: z.boolean(),
  webhookUrl: z.string(),
  webhookSecret: z.string().optional(),
});

const JenkinsConnectionStatusResponseSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.JENKINS),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  webhookUrl: z.string().nullable(),
});

const TicketFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['string', 'text', 'select', 'number', 'date']),
  required: z.boolean(),
  autoFillTerraField: z.string().optional(),
  options: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
});

const ProviderProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  issuesEnabled: z.boolean().optional(),
});

const ProviderProjectsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  cloudId: z.string().optional(),
});

const JiraCloudQuerySchema = z.object({
  cloudId: z.string().optional(),
});

const ProviderProjectsResponseSchema = z.object({
  projects: z.array(ProviderProjectSchema),
  nextCursor: z.string().nullable(),
});

const ProviderStatusOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const ProviderStatusesQuerySchema = z
  .object({
    projectId: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
  })
  .extend(JiraCloudQuerySchema.shape);

const ProviderStatusesResponseSchema = z.object({
  statuses: z.array(ProviderStatusOptionSchema),
  nextCursor: z.string().nullable(),
});

const FieldMappingSchema = z.object({
  terraField: z.string(),
  providerField: z.string(),
});

const IntegrationFlowSchema = z.enum([
  INTEGRATION_FLOWS.CREATE_TICKET_ON_FINDING,
  INTEGRATION_FLOWS.INGEST_EXTERNAL_VULNERABILITIES,
  INTEGRATION_FLOWS.SYNC_STATUS_RETEST,
  INTEGRATION_FLOWS.SYNC_STATUS_FALSE_POSITIVE,
  INTEGRATION_FLOWS.SYNC_STATUS_WONTFIX,
  INTEGRATION_FLOWS.NOTIFY_ON_REMEDIATION_PR,
  INTEGRATION_FLOWS.PUSH_FINDING_TO_SIEM,
  INTEGRATION_FLOWS.PUSH_FINDING_TO_TORQ,
  INTEGRATION_FLOWS.STAGE_PREVENTION_ON_FINDING,
  INTEGRATION_FLOWS.SYNC_EXTERNAL_NETWORK_INVENTORY,
  INTEGRATION_FLOWS.SYNC_EXTERNAL_NETWORK_DNS,
]);

export {
  OptionalFieldDefaultSchema,
  RequiredFieldDefaultSchema,
  TicketingProviderSettingsSchema,
} from './ticketing-provider-settings.schema';
export type {
  TicketingProviderSettings,
  OptionalFieldDefault,
  RequiredFieldDefault,
} from './ticketing-provider-settings.schema';

export const MessagingProviderSettingsSchema = z.strictObject({
  defaultChannelId: z.string().optional(),
});

export type MessagingProviderSettings = z.infer<
  typeof MessagingProviderSettingsSchema
>;

export const MessagingChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  isPrivate: z.boolean(),
});

export type MessagingChannel = z.infer<typeof MessagingChannelSchema>;

export const MessagingProviderChannelsResponseSchema = z.object({
  channels: z.array(MessagingChannelSchema),
  nextCursor: z.string().nullable(),
});

export type MessagingProviderChannelsResponse = z.infer<
  typeof MessagingProviderChannelsResponseSchema
>;

export type UnionProviderSettings =
  | MessagingProviderSettings
  | TicketingProviderSettings;

export const parseUnionProviderSettings = (
  data: unknown,
):
  | { success: true; data: UnionProviderSettings }
  | { success: false; error: z.ZodError } => {
  if (data === null || data === undefined) {
    return { success: true, data: {} };
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return MessagingProviderSettingsSchema.safeParse(data) as {
      success: false;
      error: z.ZodError;
    };
  }

  const record = data as Record<string, unknown>;

  if (hasTicketingProviderSettingsKeys(record)) {
    return TicketingProviderSettingsSchema.safeParse(record);
  }

  return MessagingProviderSettingsSchema.safeParse(record);
};

export const UnionProviderSettingsSchema = z
  .unknown()
  .superRefine((data, ctx) => {
    const parsed = parseUnionProviderSettings(data);

    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue({
          code: 'custom',
          message: issue.message,
          path: issue.path,
        });
      }
    }
  })
  .transform((data): UnionProviderSettings => {
    const parsed = parseUnionProviderSettings(data);

    if (!parsed.success) {
      throw new Error('Invalid provider settings');
    }

    return parsed.data;
  });

const IntegrationConfigResponseSchema = z.object({
  provider: z.string(),
  fieldMappings: z.array(FieldMappingSchema),
  enabledFlows: z.array(IntegrationFlowSchema),
  providerSettings: UnionProviderSettingsSchema.nullable(),
});

const SaveIntegrationConfigBodySchema = z.object({
  fieldMappings: z.array(FieldMappingSchema),
  enabledFlows: z.array(IntegrationFlowSchema),
  providerSettings: UnionProviderSettingsSchema.optional(),
});

export const IntegrationsContract = terraContract
  .prefix(`/${INTEGRATIONS_PATH_NAME}`)
  .router({
    getProviderProjects: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/projects',
        summary: 'List available projects/teams from the ticketing provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TicketingProviderParamSchema,
          query: ProviderProjectsQuerySchema,
        }),
      )
      .output(ProviderProjectsResponseSchema),
    getJiraSites: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/jira/sites',
        summary: 'List accessible Jira Cloud sites for the connected account',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          sites: z.array(
            z.object({
              id: z.string(),
              name: z.string(),
              url: z.string(),
            }),
          ),
          selectedSiteId: z.string().nullable(),
        }),
      ),
    selectJiraSite: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/jira/site',
        summary: 'Select the Jira Cloud site to use for this integration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          body: z.object({
            cloudId: z.string().min(1),
          }),
        }),
      )
      .output(z.object({ success: z.literal(true) }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    listProviderConfigurationProfiles: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/profiles',
        summary: 'List integration configuration profiles for a provider',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: TicketingProviderParamSchema }))
      .output(IntegrationConfigurationProfileListResponseSchema),
    getProviderConfigurationProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/profiles/{profileId}',
        summary: 'Get an integration configuration profile by id',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ProviderConfigurationProfileParamsSchema,
        }),
      )
      .output(IntegrationConfigurationProfileDtoSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createProviderConfigurationProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{provider}/profiles',
        summary: 'Create an integration configuration profile',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TicketingProviderParamSchema,
          body: CreateIntegrationConfigurationProfileBodySchema,
        }),
      )
      .output(IntegrationConfigurationProfileDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    updateProviderConfigurationProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{provider}/profiles/{profileId}',
        summary: 'Update an integration configuration profile',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ProviderConfigurationProfileParamsSchema,
          body: UpdateIntegrationConfigurationProfileBodySchema,
        }),
      )
      .output(IntegrationConfigurationProfileDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
      }),
    deleteProviderConfigurationProfile: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{provider}/profiles/{profileId}',
        summary: 'Delete a custom integration configuration profile',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ProviderConfigurationProfileParamsSchema,
        }),
      )
      .output(z.object({ success: z.literal(true) }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    createProviderConfigurationProfileSampleIssue: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{provider}/profiles/{profileId}/sample-issue',
        summary:
          'Create a labeled sample Jira issue from a saved configuration profile',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ProviderConfigurationProfileParamsSchema,
        }),
      )
      .output(IntegrationConfigurationProfileSampleIssueResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        UNAUTHORIZED: { status: 401, data: z.object({ message: z.string() }) },
      }),
    getJiraIssueTypes: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/jira/issue-types',
        summary: 'List issue types available for a Jira project',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({ projectId: z.string().min(1) })
            .extend(JiraCloudQuerySchema.shape),
        }),
      )
      .output(z.array(z.object({ value: z.string(), label: z.string() }))),
    searchJiraIssues: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/jira/issues/search',
        summary: 'Search Jira issues in a project',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              projectId: z.string().min(1),
              search: z.string().optional(),
              issueTypeId: z.string().optional(),
            })
            .extend(JiraCloudQuerySchema.shape),
        }),
      )
      .output(
        z.array(
          z.object({
            key: z.string(),
            summary: z.string(),
          }),
        ),
      ),
    getProviderFields: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/fields',
        summary: 'List available fields from the ticketing provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TicketingProviderParamSchema,
          query: z
            .object({
              projectId: z.string().optional(),
              issueTypeId: z.string().optional(),
            })
            .extend(JiraCloudQuerySchema.shape),
        }),
      )
      .output(z.array(TicketFieldSchema)),
    getProviderStatuses: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/statuses',
        summary: 'List available statuses from the ticketing provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: TicketingProviderParamSchema,
          query: ProviderStatusesQuerySchema,
        }),
      )
      .output(ProviderStatusesResponseSchema),
    getIntegrationConfig: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/config',
        summary: 'Get current tenant integration configuration',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: IntegrationOAuthProviderParamSchema }))
      .output(IntegrationConfigResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    saveIntegrationConfig: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{provider}/config',
        summary: 'Save tenant integration configuration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: IntegrationOAuthProviderParamSchema,
          body: SaveIntegrationConfigBodySchema,
        }),
      )
      .output(IntegrationConfigResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    getTicketingOAuthAuthorizeUrl: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/oauth/authorize',
        summary: 'Get OAuth authorize URL for a ticketing provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: IntegrationOAuthProviderParamSchema,
          query: z.object({
            instanceUrl: z.string().optional(),
            openerOrigin: z.string().optional(),
          }),
        }),
      )
      .output(z.object({ authorizeUrl: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    exchangeTicketingOAuthCode: terraContract
      .meta({ public: true })
      .route({
        method: 'POST',
        path: '/oauth/exchange',
        summary: 'Exchange OAuth authorization code for a ticketing provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          body: z.object({
            provider: IntegrationOAuthProviderSchema,
            code: z.string(),
            state: z.string(),
          }),
        }),
      )
      .output(z.object({ success: z.boolean(), provider: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    getTicketingConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/connection-status',
        summary: 'Get connection status for a ticketing provider',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: IntegrationOAuthProviderParamSchema }))
      .output(
        z.object({
          provider: z.string(),
          status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
          providerUserId: z.string().nullable(),
        }),
      ),
    connectGithubIssuesTicketing: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/github_issues/connect',
        summary:
          'Connect GitHub Issues via a GitHub App installation or an existing GitHub source integration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          body: z
            .object({
              gitIntegrationId: MongoObjectIdSchema.optional(),
              gitHubInstallationId: z.string().min(1).optional(),
              gitHubAppKind: z.enum(['BASIC', 'FULL']).optional(),
            })
            .superRefine((body, ctx) => {
              const hasGitIntegrationId = Boolean(body.gitIntegrationId);
              const hasInstallationId = Boolean(body.gitHubInstallationId);

              if (hasGitIntegrationId === hasInstallationId) {
                ctx.addIssue({
                  code: 'custom',
                  message:
                    'Provide exactly one of gitIntegrationId or gitHubInstallationId',
                });
              }
            }),
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    disconnectTicketingProvider: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{provider}/credential',
        summary: 'Disconnect (revoke) a ticketing provider credential',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: IntegrationOAuthProviderParamSchema }))
      .output(z.object({ success: z.boolean() })),
    getMessagingProviderChannels: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/channels',
        summary: 'List channels for a messaging integration provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MessagingProviderParamSchema,
          query: z.object({
            search: z.string().optional(),
            cursor: z.string().optional(),
            limit: z.coerce
              .number()
              .int()
              .min(1)
              .max(200)
              .optional()
              .prefault(100),
            fetchAll: z.preprocess((val) => {
              if (val === undefined || val === null) {
                return undefined;
              }

              if (val === true || val === 'true') {
                return true;
              }

              if (val === false || val === 'false') {
                return false;
              }

              return val;
            }, z.boolean().optional()),
          }),
        }),
      )
      .output(MessagingProviderChannelsResponseSchema),
    resolveMessagingProviderChannels: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/channels/resolve',
        summary: 'Resolve messaging channels by id for saved configuration',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: MessagingProviderParamSchema,
          query: z.object({
            channelIds: z.preprocess(
              (val) => {
                if (val === undefined || val === null || val === '') {
                  return [];
                }

                if (Array.isArray(val)) {
                  return val.flatMap((entry) =>
                    typeof entry === 'string'
                      ? entry
                          .split(',')
                          .map((part) => part.trim())
                          .filter(Boolean)
                      : [],
                  );
                }

                if (typeof val === 'string') {
                  return val
                    .split(',')
                    .map((part) => part.trim())
                    .filter(Boolean);
                }

                return [];
              },
              z.array(z.string().min(1)).max(25),
            ),
          }),
        }),
      )
      .output(z.object({ channels: z.array(MessagingChannelSchema) })),
    listApplicationMessagingChannels: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/applications/channels',
        summary:
          'List per-application messaging channel overrides for a provider',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: MessagingProviderParamSchema }))
      .output(
        z.object({
          overrides: z.array(
            z.object({
              applicationId: z.string(),
              channelId: z.string(),
            }),
          ),
        }),
      ),
    getApplicationMessagingChannel: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{provider}/applications/{applicationId}/channel',
        summary: 'Get per-application messaging channel override',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationMessagingChannelParamSchema }))
      .output(z.object({ channelId: z.string() }))
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
    putApplicationMessagingChannel: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{provider}/applications/{applicationId}/channel',
        summary: 'Set per-application messaging channel override',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ApplicationMessagingChannelParamSchema,
          body: z.object({ channelId: z.string().min(1) }),
        }),
      )
      .output(z.object({ success: z.literal(true) })),
    deleteApplicationMessagingChannel: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{provider}/applications/{applicationId}/channel',
        summary: 'Clear per-application messaging channel override',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: ApplicationMessagingChannelParamSchema }))
      .output(z.object({ success: z.literal(true) })),
    connectSearchEngine: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/search-engine/{provider}/connect',
        summary: 'Connect a search engine provider with API credentials',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SearchEngineProviderParamSchema,
          body: SearchEngineConnectBodySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
      }),
    getSearchEngineConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/search-engine/{provider}/connection-status',
        summary: 'Get connection status for a search engine provider',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: SearchEngineProviderParamSchema }))
      .output(
        z.object({
          provider: z.string(),
          status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
          providerUserId: z.string().nullable(),
        }),
      ),
    disconnectSearchEngine: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/search-engine/{provider}/credential',
        summary: 'Disconnect (revoke) a search engine provider credential',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: SearchEngineProviderParamSchema }))
      .output(z.object({ success: z.boolean() })),
    connectWiz: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect Wiz with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: WizConnectBodySchema }))
      .output(
        z.object({
          success: z.boolean(),
          automationRulesCreated: z.boolean(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getWizConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Wiz connection status',
        inputStructure: 'detailed',
      })
      .output(WizConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getWizMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/mappings`,
        summary: 'Get Wiz application mappings',
        inputStructure: 'detailed',
      })
      .output(WizMappingsResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getWizMappingSuggestions: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/mapping-suggestions`,
        summary:
          'Suggest Wiz resource mappings from connected git repositories',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: WizMappingSuggestionsQuerySchema }))
      .output(WizMappingSuggestionsResponseSchema)
      .errors({
        NOT_FOUND: {
          status: 404,
          data: z.looseObject({ message: z.string().optional() }),
        },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getWizSelectableObjects: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/objects`,
        summary: 'Search Wiz objects for application mapping',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: WizSelectableObjectsQuerySchema }))
      .output(WizSelectableObjectsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: {
          status: 404,
          data: z.looseObject({ message: z.string().optional() }),
        },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    saveWizMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/mappings`,
        summary: 'Save Wiz application mappings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: WizMappingsResponseSchema }))
      .output(WizMappingsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerWizScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Trigger a proactive scan of existing Wiz issues and threats',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          success: z.boolean(),
          fetched: z.number(),
          published: z.number(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectWiz: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${WIZ_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Wiz and remove automation rules',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectHackerone: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${HACKERONE_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect HackerOne with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: HackeroneConnectBodySchema }))
      .output(BugBountyConnectResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getHackeroneConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${HACKERONE_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get HackerOne connection status',
        inputStructure: 'detailed',
      })
      .output(HackeroneConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerHackeroneScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${HACKERONE_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Queue a background scan of existing HackerOne reports',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyScanBodySchema }))
      .output(BugcrowdScanTriggerResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectHackerone: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${HACKERONE_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect HackerOne credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyConnectionIdBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    renameHackeroneConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: `/${HACKERONE_INTEGRATION_PATH_NAME}/connection`,
        summary: 'Rename a HackerOne connection',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyRenameConnectionBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectBugcrowd: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${BUGCROWD_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect Bugcrowd with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugcrowdConnectBodySchema }))
      .output(BugBountyConnectResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getBugcrowdConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${BUGCROWD_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Bugcrowd connection status',
        inputStructure: 'detailed',
      })
      .output(BugcrowdConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerBugcrowdScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${BUGCROWD_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Queue a background scan of existing Bugcrowd submissions',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyScanBodySchema }))
      .output(BugcrowdScanTriggerResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectBugcrowd: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${BUGCROWD_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Bugcrowd credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyConnectionIdBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    renameBugcrowdConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: `/${BUGCROWD_INTEGRATION_PATH_NAME}/connection`,
        summary: 'Rename a Bugcrowd connection',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BugBountyRenameConnectionBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    listBugBountyImportFailedRows: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/signal-import/failed-rows',
        summary: 'List failed bug bounty import rows for export',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              batchId: z.string().optional(),
              scanRunKey: z.string().optional(),
            })
            .refine(
              (query) => Boolean(query.batchId || query.scanRunKey),
              'batchId or scanRunKey is required',
            ),
        }),
      )
      .output(BugBountyImportFailedRowsResponseSchema)
      .errors({
        NOT_FOUND: {
          status: 404,
          data: z.object({ message: z.string() }),
        },
      }),
    connectJenkins: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${JENKINS_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Generate and store the Jenkins deployment webhook secret',
        inputStructure: 'detailed',
      })
      .output(JenkinsConnectResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getJenkinsConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${JENKINS_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Jenkins connection status',
        inputStructure: 'detailed',
      })
      .output(JenkinsConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectJenkins: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${JENKINS_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Jenkins and revoke the webhook secret',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectUpwind: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect Upwind with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: UpwindConnectBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getUpwindConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Upwind connection status',
        inputStructure: 'detailed',
      })
      .output(UpwindConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getUpwindMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/mappings`,
        summary: 'Get Upwind application mappings',
        inputStructure: 'detailed',
      })
      .output(UpwindMappingsResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getUpwindSelectableResources: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/resources`,
        summary: 'List Upwind resources for application mapping',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: UpwindSelectableResourcesQuerySchema }))
      .output(UpwindSelectableResourcesResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    saveUpwindMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/mappings`,
        summary: 'Save Upwind application mappings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: UpwindMappingsResponseSchema }))
      .output(UpwindMappingsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerUpwindScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Trigger a proactive scan of Upwind findings',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          success: z.boolean(),
          fetched: z.number(),
          published: z.number(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectUpwind: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${UPWIND_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Upwind credentials',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectXpanse: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect Cortex Xpanse with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: XpanseConnectBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getXpanseConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Cortex Xpanse connection status',
        inputStructure: 'detailed',
      })
      .output(XpanseConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    updateXpanseSettings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/settings`,
        summary: 'Update Cortex Xpanse sync settings',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: XpanseUpdateSettingsBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerXpanseAssetImport: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/asset-import`,
        summary: 'Trigger a Cortex Xpanse asset inventory import',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          success: z.boolean(),
          netblocks: z.number(),
          domains: z.number(),
          hosts: z.number(),
          networkServices: z.number(),
          software: z.number(),
          cveSignals: z.number(),
          skippedOutOfScope: z.number(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerXpanseScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Trigger a proactive scan of existing Cortex Xpanse alerts',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          success: z.boolean(),
          fetched: z.number(),
          published: z.number(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectXpanse: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${XPANSE_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Cortex Xpanse credentials',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectCrowdstrike: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${CROWDSTRIKE_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect CrowdStrike with API credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CrowdStrikeConnectBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getCrowdstrikeConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${CROWDSTRIKE_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get CrowdStrike connection status',
        inputStructure: 'detailed',
      })
      .output(CrowdStrikeConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerCrowdstrikeScan: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${CROWDSTRIKE_INTEGRATION_PATH_NAME}/scan`,
        summary: 'Trigger a proactive scan of CrowdStrike external assets',
        inputStructure: 'detailed',
      })
      .output(
        z.object({
          success: z.boolean(),
          fetched: z.number(),
          published: z.number(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectCrowdstrike: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${CROWDSTRIKE_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect CrowdStrike credentials',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectSiem: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${SIEM_INTEGRATION_PATH_NAME}/{provider}/connect`,
        summary: 'Connect a SIEM provider with ingest credentials',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SiemProviderParamSchema,
          body: SiemConnectBodySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getSiemConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${SIEM_INTEGRATION_PATH_NAME}/{provider}/connection-status`,
        summary: 'Get SIEM provider connection status',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: SiemProviderParamSchema }))
      .output(SiemConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectSiem: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${SIEM_INTEGRATION_PATH_NAME}/{provider}/credential`,
        summary: 'Disconnect SIEM provider credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: SiemProviderParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectTorq: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${TORQ_INTEGRATION_PATH_NAME}/connect`,
        summary: 'Connect Torq with a webhook trigger URL',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: TorqConnectBodySchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getTorqConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${TORQ_INTEGRATION_PATH_NAME}/connection-status`,
        summary: 'Get Torq connection status',
        inputStructure: 'detailed',
      })
      .output(TorqConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectTorq: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${TORQ_INTEGRATION_PATH_NAME}/credential`,
        summary: 'Disconnect Torq credentials',
        inputStructure: 'detailed',
      })
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectPrevention: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/{controlType}/{provider}/connect`,
        summary: 'Create a prevention connection with credentials and coverage',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionProviderParamSchema,
          body: PreventionConnectBodySchema,
        }),
      )
      .output(PreventionConnectionDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    listPreventionConnections: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/connections`,
        summary: 'List prevention connections',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: PreventionListConnectionsQuerySchema }))
      .output(z.array(PreventionConnectionDtoSchema))
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    ensureAwsWafPreventionConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/connections/aws-wafv2/ensure`,
        summary:
          'Ensure a tenant-level AWS WAFv2 prevention connection exists for the AWS cloud flow',
        inputStructure: 'detailed',
      })
      .output(PreventionConnectionDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    discoverPreventionWafTargets: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/waf/targets`,
        summary:
          'Discover routable WAF targets (e.g. AWS Web ACLs, Cloudflare zones) for a WAF vendor',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: DiscoverPreventionWafTargetsQuerySchema }))
      .output(DiscoverPreventionWafTargetsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerPreventionInventorySync: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/connections/{connectionId}/inventory-sync`,
        summary:
          'Run Cloudflare inventory sync for a prevention connection now',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionConnectionIdParamSchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    updatePreventionConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'PATCH',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/connections/{connectionId}`,
        summary: 'Update a prevention connection',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionConnectionIdParamSchema,
          body: UpdatePreventionConnectionBodySchema,
        }),
      )
      .output(PreventionConnectionDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    deletePreventionConnection: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'DELETE',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/connections/{connectionId}`,
        summary: 'Delete a prevention connection',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionConnectionIdParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getPreventionConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/{controlType}/{provider}/connection-status`,
        summary: 'Get prevention provider connection status',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionProviderParamSchema }))
      .output(PreventionConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectPrevention: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'DELETE',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/{controlType}/{provider}/credential`,
        summary: 'Disconnect all prevention connections for a provider',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionProviderParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    listPreventionTargetMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/target-mappings`,
        summary: 'List prevention target mappings',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: ListPreventionTargetMappingsQuerySchema }))
      .output(z.array(PreventionTargetMappingDtoSchema))
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getApplicationWafSuggestions: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/applications/{applicationId}/waf-suggestions`,
        summary:
          'Suggest WAF providers for an application from gateway detection',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionApplicationIdParamSchema }))
      .output(PreventionWafSuggestionsResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    createPreventionTargetMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/target-mappings`,
        summary: 'Create a prevention target mapping override',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CreatePreventionTargetMappingBodySchema }))
      .output(PreventionTargetMappingDtoSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    deletePreventionTargetMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'DELETE',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/target-mappings/{mappingId}`,
        summary: 'Delete a prevention target mapping',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionTargetMappingIdParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    listPreventionControls: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls`,
        summary: 'List prevention controls',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: PreventionListQuerySchema }))
      .output(z.array(PreventionControlDtoSchema))
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getPreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}`,
        summary: 'Get a prevention control',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionControlIdParamSchema }))
      .output(PreventionControlDtoSchema)
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    previewPreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/preview`,
        summary: 'Preview a staged prevention control',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionControlIdParamSchema }))
      .output(
        z.object({
          diff: z.record(z.string(), z.unknown()),
          summary: z.string(),
        }),
      )
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    testPreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/test`,
        summary: 'Test an active prevention control with custom HTTP probes',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionControlIdParamSchema,
          body: PreventionControlTestBodySchema,
        }),
      )
      .output(PreventionControlTestResultSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getPreventionControlTestTemplate: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_PREVENTION.name],
      })
      .route({
        method: 'GET',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/test/template`,
        summary: 'Get default HTTP probes for testing a prevention control',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionControlIdParamSchema }))
      .output(PreventionControlTestTemplateSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    activatePreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.APPROVE_PREVENTION_CONTROL.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/activate`,
        summary: 'Activate a staged prevention control',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionControlIdParamSchema,
          body: PreventionActivateBodySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    rejectPreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.APPROVE_PREVENTION_CONTROL.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/reject`,
        summary: 'Reject a staged prevention control',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: PreventionControlIdParamSchema,
          body: PreventionRejectBodySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    deactivatePreventionControl: terraContract
      .meta({
        permissions: [PERMISSIONS.APPROVE_PREVENTION_CONTROL.name],
      })
      .route({
        method: 'POST',
        path: `/${PREVENTION_INTEGRATION_PATH_NAME}/controls/{controlId}/deactivate`,
        summary: 'Deactivate an active prevention control',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: PreventionControlIdParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    connectCloud: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/connect`,
        summary: 'Connect a cloud provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: CloudProviderParamSchema,
          body: CloudConnectBodySchema,
        }),
      )
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    getCloudConnectionStatus: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/connection-status`,
        summary: 'Get cloud provider connection status',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: CloudProviderParamSchema }))
      .output(CloudConnectionStatusResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    disconnectCloud: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/credential`,
        summary: 'Disconnect cloud provider credentials',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: CloudProviderParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    updateCloudFlows: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/flows`,
        summary: 'Enable or disable a cloud provider integration flow',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: CloudProviderParamSchema,
          body: CloudUpdateFlowsBodySchema,
        }),
      )
      .output(CloudConnectionStatusResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    updateCloudInventorySettings: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/inventory-settings`,
        summary: 'Update cloud inventory target surfaces',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: CloudProviderParamSchema,
          body: CloudUpdateInventorySettingsBodySchema,
        }),
      )
      .output(CloudConnectionStatusResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    triggerCloudInventorySync: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: `/${CLOUD_INTEGRATION_PATH_NAME}/{provider}/inventory-sync`,
        summary: 'Run cloud inventory sync for a connected provider now',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: CloudProviderParamSchema }))
      .output(z.object({ success: z.boolean() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
  });
